import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';

import { IClub, IUser } from '@eagerdog/interfaces';
import { Constants } from '@eagerdog/constants';
import { helperService } from '../../services/helper.service';
import { localStorageService } from '../../services/local-storage.service';
import { apiService } from '../../services/api.service';
import { toast } from '../../components/Toast/ToastManager';

import ClubSettings from '../../components/ClubSettings/ClubSettings';
import GoogleMapComponent from '../KennelClub/components/GoogleMapComponent/GoogleMapComponent';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import Checkbox from "../../components/Checkbox/Checkbox";
import RadioList from '../../components/RadioList/RadioList';
import Dropdown, { IOption, States, Provinces } from '../../components/Dropdown/Dropdown';
import Phone from '../../components/Phone/Phone';

import './AdminSettings.scss';

// Icons - properly imported SVG files
import flagIcon from '../../assets/icons/flag.svg';
import keyIcon from '../../assets/icons/key.svg';

interface IProps {}

const AdminSettings: React.FC<IProps> = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const user: IUser = helperService.getUser();

  const [activeTab, setActiveTab] = useState<number>(() => {
    // Check if there's a saved tab from session storage (after email change reload)
    const savedTab = sessionStorage.getItem('adminSettingsActiveTab');
    if (savedTab) {
      sessionStorage.removeItem('adminSettingsActiveTab'); // Clear it after reading
      return parseInt(savedTab, 10);
    }
    return 0;
  });
  const [currentClub, setCurrentClub] = useState<IClub | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // State variables copied from Account.tsx
  const [displayName, setDisplayName] = useState<string>('');
  const [website, setWebsite] = useState<string>('');
  const [clubNumber, setClubNumber] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [userInfoChanged, setUserInfoChanged] = useState<boolean>(false);
  const [isUpdatingPersonalInfo, setIsUpdatingPersonalInfo] = useState<boolean>(false);

  // Location state
  const [line1, setLine1] = useState<string>('');
  const [line2, setLine2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [country, setCountry] = useState<IOption>({ value: "Canada", id: "Canada" });
  const [state, setState] = useState<IOption>({ value: "", id: "" });
  const [postalCode, setPostalCode] = useState<string>('');

  // Password state
  const [password, setPassword] = useState<string>('');
  const [repeat, setRepeat] = useState<string>('');

  // Email change state
  const [newEmail, setNewEmail] = useState<string>('');
  const [emailVerificationCode, setEmailVerificationCode] = useState<string>('');
  const [isEmailChangeRequested, setIsEmailChangeRequested] = useState<boolean>(false);
  const [isRequestingEmailChange, setIsRequestingEmailChange] = useState<boolean>(false);
  const [isVerifyingEmailChange, setIsVerifyingEmailChange] = useState<boolean>(false);

  // Donations
  const [enableDonations, setEnableDonations] = useState<boolean>(true);
  const [donationAmount1, setDonationAmount1] = useState<string>("0");
  const [donationAmount2, setDonationAmount2] = useState<string>("0");
  const [donationAmount3, setDonationAmount3] = useState<string>("0");

  // Advanced
  const [autoChargeWaitlist, setAutoChargeWaitlist] = useState<boolean>(true);
  const [platformFeesBy, setPlatformFeesBy] = useState<string>(Constants.platform_fees_by.owner);
  const [isSavingAdvanced, setIsSavingAdvanced] = useState<boolean>(false);

  const tabs = ['Location', 'Branding', 'Account', 'Donations', 'Advanced'];

  // Load club data
  useEffect(() => {
    if (user.user_type === Constants.user_type.club_manager) {
      apiService.getCurrentClub().then((club) => {
        setCurrentClub(club);
        populateClubData(club);
        setLoading(false);
      }).catch((error) => {
        toast.show({
          title: "Error",
          content: "Unable to load club information.",
          type: "fail"
        });
        setLoading(false);
      });
    } else if (clubId) {
      // For capacity-based access (dog_owner with admin capacity), fetch by clubId
      apiService.getClub(clubId).then((club) => {
        setCurrentClub(club);
        populateClubData(club);
        setLoading(false);
      }).catch((error) => {
        toast.show({
          title: "Error",
          content: "Unable to load club information.",
          type: "fail"
        });
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [clubId, user.user_type]);

  const populateClubData = (club: IClub) => {
    // Basic info
    setDisplayName(club.club_name || '');
    setWebsite(club.website || '');
    setClubNumber(club.club_number || '');
    setPhone(club.phone || '');
    
    // Location
    setLine1(club.address?.line1 || '');
    setLine2(club.address?.line2 || '');
    setCity(club.address?.city || '');
    setPostalCode(club.address?.postal_code || '');

    // Set country and state - match Account.tsx logic
    if (club.address?.country === 'US' || club.address?.country === 'USA') {
      setCountry({ value: "USA", id: "USA" });
    } else {
      setCountry({ value: "Canada", id: "Canada" });
    }
    
    if (club.address?.state) {
      setState({ value: club.address.state, id: club.address.state });
    }

    // Donations
    setEnableDonations(club.enable_donations ?? true);
    setDonationAmount1(club.donation_amounts_preset?.[0] ? (club.donation_amounts_preset[0] / 100).toString() : "5");
    setDonationAmount2(club.donation_amounts_preset?.[1] ? (club.donation_amounts_preset[1] / 100).toString() : "10");
    setDonationAmount3(club.donation_amounts_preset?.[2] ? (club.donation_amounts_preset[2] / 100).toString() : "20");

    // Advanced
    setAutoChargeWaitlist(club.auto_charge_waitlist !== false);
    setPlatformFeesBy(club.platform_fees_by || Constants.platform_fees_by.owner);
  };

  // Reuse updateUserInfo from Account.tsx
  const updateUserInfo = useCallback(() => {
    setIsUpdatingPersonalInfo(true);
    const updateData = {
      display_name: displayName,
      website: helperService.normalizeUrl(website) || "",
      club_number: clubNumber,
      phone: phone,
      address: {
        line1,
        line2,
        city,
        state: state.id,
        country: country.id,
        postal_code: postalCode
      }
    };

    apiService.updateSelf(updateData).then((response: any) => {
      toast.show({
        title: "Account",
        content: "Successfully updated account.",
        duration: 10000,
        type: "success"
      });
      setUserInfoChanged(false);
      
      // Also update the club
      if (currentClub) {
        const clubUpdateData = {
          club_name: displayName,
          website: helperService.normalizeUrl(website) || "",
          club_number: clubNumber,
          phone: phone,
          address: {
            line1,
            line2,
            city,
            state: state.id,
            country: country.id,
            postal_code: postalCode
          }
        };
        
        // For now, use updateCurrentClub for both cases
        // TODO: Implement proper club update API for capacity-based access
        apiService.updateCurrentClub(clubUpdateData).then((clubResponse) => {
          setCurrentClub(clubResponse);
        }).catch((error) => {
        });
      }
      
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Account",
        content: "Unable to update account.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    }).finally(() => {
      setIsUpdatingPersonalInfo(false);
    });
  }, [displayName, website, clubNumber, phone, line1, line2, city, state, country, postalCode, currentClub]);

  // Reuse updateUserPassword from Account.tsx
  const updateUserPassword = useCallback(() => {
    apiService.updateSelfPassword({ new_password: password }).then((response) => {
      toast.show({
        title: "Password",
        content: "Successfully updated password.",
        duration: 10000,
        type: "success"
      });
      setPassword('');
      setRepeat('');
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Password",
        content: "Unable to update password.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    });
  }, [password]);

  const handleDonationSettingsUpdate = () => {
    const amounts = [
      Number(donationAmount1) * 100,
      Number(donationAmount2) * 100,
      Number(donationAmount3) * 100,
    ]

    if (amounts.some((amount) => amount < 500)) {
      toast.show({
        title: "Donation Settings",
        content: "Minimum donation amount must be $5.",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    if (currentClub) {
      const payload = {
        enable_donations: enableDonations,
        donation_amounts_preset: amounts,
      };

      apiService.updateClubDonationSettings(currentClub._id, payload)
        .then((clubResponse) => {
          setCurrentClub(clubResponse);
          toast.show({
            title: "Donation Settings",
            content: "Donation amounts updated successfully.",
            duration: 5000,
            type: "success"
          });
        })
        .catch((error) => {
          toast.show({
            title: "Donation Settings",
            content: "Unable to update donation settings.",
            duration: 5000,
            type: "fail",
            errorDetails: error
          });
        });
    }
  }

  // Email change functions
  const requestEmailChange = () => {
    if (!newEmail || newEmail.toLowerCase() === user.email.toLowerCase()) {
      toast.show({
        title: "Email Change",
        content: "Please enter a different email address.",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    setIsRequestingEmailChange(true);
    apiService.emailChangeRequest(newEmail).then(() => {
      toast.show({
        title: "Email Change",
        content: "A verification code has been sent to your new email address.",
        duration: 10000,
        type: "success"
      });
      setIsEmailChangeRequested(true);
    }).catch((error: AxiosError) => {
      toast.show({
        title: "Email Change",
        content: "Unable to request email change.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    }).finally(() => {
      setIsRequestingEmailChange(false);
    });
  };

  const verifyEmailChange = () => {
    if (!emailVerificationCode) {
      toast.show({
        title: "Email Change",
        content: "Please enter the verification code.",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    setIsVerifyingEmailChange(true);
    apiService.emailChangeVerify(emailVerificationCode).then((updatedUser) => {
      toast.show({
        title: "Email Change",
        content: "Your email has been successfully updated.",
        duration: 10000,
        type: "success"
      });
      // Update local storage with new user data
      localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
      // Reset state
      setNewEmail('');
      setEmailVerificationCode('');
      setIsEmailChangeRequested(false);
      // Save current tab to session storage so it persists after reload
      sessionStorage.setItem('adminSettingsActiveTab', '2'); // Account tab is index 2
      // Reload to reflect changes
      window.location.reload();
    }).catch((error: AxiosError<any>) => {
      const errorMessage = error.response?.data?.message || "The verification code is invalid or may have expired. Please request a new code.";
      toast.show({
        title: "Email Change",
        content: errorMessage,
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    }).finally(() => {
      setIsVerifyingEmailChange(false);
    });
  };

  const cancelEmailChange = () => {
    setNewEmail('');
    setEmailVerificationCode('');
    setIsEmailChangeRequested(false);
  };

  // Reuse render functions from Account.tsx with minimal modifications
  const renderLocation = () => {
    return (
      <div className="settingsTab">
        <form className="inputs" onSubmit={(e) => { e.preventDefault(); updateUserInfo(); }}>
          <div className="inputRow">
            <Input label="Address Line 1" id="line1" type="text" onChange={(e) => { e.stopPropagation(); setLine1(e.target.value); setUserInfoChanged(true); }} defaultValue={line1} placeholder="Address Line 1" />
          </div>
          <div className="inputRow">
            <Input label="Line 2" id="line2" type="text" onChange={(e) => { e.stopPropagation(); setLine2(e.target.value); setUserInfoChanged(true); }} defaultValue={line2} placeholder="Line 2" />
          </div>
          <div className="inputRow">
            <Input label="City" id="city" type="text" onChange={(e) => { e.stopPropagation(); setCity(e.target.value); setUserInfoChanged(true); }} defaultValue={city} placeholder="City" /> 
          </div>
          <div className="inputRow">            
            <Dropdown value={state} onChange={(e: any, value: IOption) => { setState(value); setUserInfoChanged(true); }} icon={flagIcon} label="Province / State" options={country.value === "USA" ? States : Provinces} placeholder="Choose your state/province of residence" />
          </div>
          <div className="inputRow">
            <Dropdown value={country} onChange={(e: any, value: IOption) => { setCountry(value); setUserInfoChanged(true); }} icon={flagIcon} label="Country" options={[{ value: "Canada", id: "Canada" }, { value: "USA", id: "USA" }]} placeholder="Choose your country of residence" />
          </div>
          <div className="inputRow">
            <Input label="Zip" id="postalCode" type="text" onChange={(e) => { e.stopPropagation(); setPostalCode(e.target.value); setUserInfoChanged(true); }} defaultValue={postalCode} placeholder="Postal Code" />
          </div>
          <div className="actions">
            <Button type="submit" disabled={!userInfoChanged} isLoading={isUpdatingPersonalInfo}>Update Location</Button>
          </div>
          {currentClub && (
            <div className="inputRow">
              <div className="mapSection">
                <div className="mapLabel">Map Location</div>
                <div className="mapDescription">Automatically updates based on the address above</div>
                <div className="mapWrapper">
                  {<GoogleMapComponent
                    club={{
                      _id: currentClub._id,
                      club_name: displayName,
                      email: user.email,
                      phone: phone,
                      address: {
                        line1,
                        line2,
                        city,
                        state: state.id,
                        country: country.id,
                        postal_code: postalCode
                      },
                      coordinates: undefined
                    }}
                    readonly={true}
                  />}
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    );
  }

  const renderCustomization = () => {
    if (!currentClub) {
      return (
        <div className="settingsTab">
          <div className="error">
            <h3>Club information unavailable</h3>
            <p>Unable to load club data. Please try refreshing the page.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="settingsTab">
        <div className="inputs">
          <ClubSettings 
            club={currentClub} 
            clubId={clubId}
            onClubUpdate={(updatedClub) => {
              setCurrentClub(updatedClub);
            }} 
          />
        </div>
      </div>
    );
  }

  const renderBasicInformation = () => {
    const passwordsMatch = password.length > 0 && repeat.length > 0 && password === repeat;
    
    return (
      <div className="settingsTab">
        
        {/* Basic Information Section */}
        <div className="section">
          <div className="subtitle">Basic Information</div>
          <form className="inputs" onSubmit={(e) => { e.preventDefault(); updateUserInfo(); }}>
            <div className="inputRow">
              <Input required label="Club Name" id="displayName" type="text" onChange={(e) => { e.stopPropagation(); setDisplayName(e.target.value); setUserInfoChanged(true); }} defaultValue={displayName} placeholder="Update your club's name" />
            </div>
            <div className="inputRow">
              <Phone label="Phone Number" value={phone} onChange={(e: any) => { setPhone(e); setUserInfoChanged(true); }} />
            </div>
            <div className="inputRow">
              <Input label="Website" id="website" type="text" onChange={(e) => { e.stopPropagation(); setWebsite(e.target.value); setUserInfoChanged(true); }} defaultValue={website} placeholder="Update your website" />            
            </div>
            <div className="inputRow">
              <Input label="Club Number" id="clubNumber" type="text" onChange={(e) => { e.stopPropagation(); setClubNumber(e.target.value); setUserInfoChanged(true); }} defaultValue={clubNumber} placeholder="Enter your club number" />            
            </div>
            <div className="actions">
              <Button type="submit" disabled={!userInfoChanged} isLoading={isUpdatingPersonalInfo}>Save Basic Information</Button>
            </div>
          </form>
        </div>

        {/* Change Email Section */}
        <div className="section">
          <div className="subtitle">Change Email</div>
          <div className="inputs">
            {!isEmailChangeRequested ? (
              <>
                <div className="inputRow">
                  <Input 
                    label="Current Email" 
                    type="email" 
                    disabled 
                    defaultValue={user.email} 
                    onChange={() => {}}
                  />
                </div>
                <div className="inputRow">
                  <Input 
                    label="New Email Address" 
                    type="email" 
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)} 
                    placeholder="Enter your new email address" 
                  />
                </div>
                <div className="actions">
                  <Button 
                    disabled={!newEmail || newEmail.toLowerCase() === user.email.toLowerCase()} 
                    onClick={requestEmailChange} 
                    isLoading={isRequestingEmailChange}
                  >
                    Send Verification Code
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="inputRow">
                  <div className="emailChangeInfo">
                    A verification code has been sent to <strong>{newEmail}</strong>. Please check your inbox and enter the code below.
                  </div>
                </div>
                <div className="inputRow">
                  <Input 
                    label="Verification Code" 
                    type="text" 
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value)} 
                    placeholder="Enter the verification code" 
                  />
                </div>
                <div className="actions">
                  <Button 
                    className="secondary"
                    onClick={cancelEmailChange}
                  >
                    Cancel
                  </Button>
                  <Button 
                    disabled={!emailVerificationCode} 
                    onClick={verifyEmailChange} 
                    isLoading={isVerifyingEmailChange}
                  >
                    Verify & Update Email
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Password Section */}
        <div className="section">
          <div className="subtitle">Change Password</div>
          <div className="inputs">
            <div className="inputRow">
              <Input onChange={(e) => { setPassword(e.target.value); }} value={password} required type="password" icon={keyIcon} label="New Password" placeholder="Enter your new password" />
            </div>
            <div className="inputRow">
              <Input onChange={(e) => { setRepeat(e.target.value); }} value={repeat} required type="password" icon={keyIcon} label="Confirm Password" placeholder="Enter your password again" />
            </div>
            <div className="actions">
              <Button disabled={!passwordsMatch} onClick={updateUserPassword}>Update Password</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderDonationSettings = () => {
    return (
      <div className="settingsTab">
        <div className="inputs">
          <div className="inputRow">
            <Checkbox id="club-donations" label="Enable donations" value={enableDonations} onChange={() => {
              setEnableDonations(!enableDonations);
            }}/>
          </div>

          <div className="inputRow">
            <Input onChange={(e) => {
              setDonationAmount1(e.target.value);
            }} value={donationAmount1} required disabled={!enableDonations} type="number" min={5} label="Donation amount 1"
                   placeholder="Please enter donation amount value"/>
          </div>
          <div className="inputRow">
            <Input onChange={(e) => {
              setDonationAmount2(e.target.value);
            }} value={donationAmount2} required disabled={!enableDonations} type="number" min={5} label="Donation amount 2"
                   placeholder="Please enter donation amount value"/>
          </div>
          <div className="inputRow">
            <Input onChange={(e) => {
              setDonationAmount3(e.target.value);
            }} value={donationAmount3} required disabled={!enableDonations} type="number" min={5} label="Donation amount 3"
                   placeholder="Please enter donation amount value"/>
          </div>

          <Button onClick={handleDonationSettingsUpdate}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  const handleAdvancedSettingsUpdate = () => {
    if (!currentClub) return;
    setIsSavingAdvanced(true);
    apiService.updateClub(currentClub._id, {
      auto_charge_waitlist: autoChargeWaitlist,
      platform_fees_by: platformFeesBy,
    })
      .then((clubResponse) => {
        setCurrentClub(clubResponse);
        toast.show({
          title: "Advanced Settings",
          content: "Advanced settings updated successfully.",
          duration: 5000,
          type: "success"
        });
      })
      .catch((error) => {
        toast.show({
          title: "Advanced Settings",
          content: "Unable to update advanced settings.",
          duration: 5000,
          type: "fail",
          errorDetails: error
        });
      })
      .finally(() => setIsSavingAdvanced(false));
  };

  const renderAdvancedSettings = () => {
    return (
      <div className="settingsTab">
        <div className="inputs">
          <div className="inputRow">
            <Checkbox
              id="auto-charge-waitlist"
              label="Auto Charge"
              value={autoChargeWaitlist}
              onChange={() => setAutoChargeWaitlist(!autoChargeWaitlist)}
            />
          </div>
          <div className="inputRow">
            <p style={{ margin: 0, fontSize: 13, color: '#666', lineHeight: 1.5 }}>
              When enabled, Eager Dog will automatically charge the payment method on file when
              a waitlisted entry is approved or a random draw entry gets in. This eliminates
              the manual payment step for entrants, streamlining the process for both clubs
              and participants.
            </p>
          </div>

          <div className="inputRow" style={{ borderTop: '1px solid #eee', paddingTop: 16, marginTop: 8 }}>
            <label style={{ fontWeight: 600, fontSize: 14, display: 'block', marginBottom: 10 }}>
              Who Pays Eager Dog Fees for Event entries?
            </label>
            <RadioList
              selected={platformFeesBy === Constants.platform_fees_by.club ? 1 : 0}
              onSelect={(index) => setPlatformFeesBy(index === 1 ? Constants.platform_fees_by.club : Constants.platform_fees_by.owner)}
              list={[
                {
                  id: Constants.platform_fees_by.owner,
                  label: "Dog Owner pays fees (Default)",
                  action: { label: "", onClick: () => {} },
                },
                {
                  id: Constants.platform_fees_by.club,
                  label: "Club absorbs fees",
                  action: { label: "", onClick: () => {} },
                },
              ]}
            />
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#666', lineHeight: 1.5 }}>
              {platformFeesBy === Constants.platform_fees_by.club
                ? "Eager Dog fees are deducted from the club's payout. Registrants only pay the entry fees you set."
                : "Eager Dog fees are added on top of the entry fees and paid by the registrant at checkout."}
            </p>
          </div>

          <Button onClick={handleAdvancedSettingsUpdate} isLoading={isSavingAdvanced}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="AdminSettings">
        <header className="settingsHeader">
          <h1>Settings</h1>
          <p className="subtitle">Manage club location, branding, and account settings</p>
        </header>

        <div className="settingsContent">
          <div className="customTabs">
            {tabs.map((label, index) => (
              <button
                key={index}
                className={`tab ${activeTab === index ? 'active' : ''}`}
                onClick={() => setActiveTab(index)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="tabContent">
            <div className="tabSection">
              <div className="tabDescription skeleton">
                <div className="skeleton-text"></div>
              </div>
              <div className="settingsTab">
                <div className="inputs">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="inputRow skeleton">
                      <div className="skeleton-label"></div>
                      <div className="skeleton-input"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="AdminSettings">
      <header className="settingsHeader">
        <h1>Settings</h1>
        <p className="subtitle">Manage club location, branding, and account settings</p>
      </header>

      <div className="settingsContent">
        <div className="customTabs">
          {tabs.map((label, index) => (
            <button
              key={index}
              className={`tab ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="tabContentadmin">
          {activeTab === 0 && (
            <div className="tabSectionadmin">
              <div className="tabDescriptionadmin">
                <p>Set your club's address and location information</p>
              </div>
              {renderLocation()}
            </div>
          )}
          {activeTab === 1 && (
            <div className="tabSectionadmin">
              <div className="tabDescriptionadmin">
                <p>Customize your club's public page with logo, cover image, and description</p>
              </div>
              {renderCustomization()}
            </div>
          )}
          {activeTab === 2 && (
            <div className="tabSection">
              <div className="tabDescriptionadmin">
                <p>Manage your club's essential details and security</p>
              </div>
              {renderBasicInformation()}
            </div>
          )}
          {activeTab === 3 && (
            <div className="tabSection">
              <div className="tabDescriptionadmin">
                <p>Manage your club's donations</p>
              </div>
              {renderDonationSettings()}
            </div>
          )}
          {activeTab === 4 && (
            <div className="tabSection">
              <div className="tabDescriptionadmin">
                <p>Configure advanced payment and registration settings</p>
              </div>
              {renderAdvancedSettings()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;